import openWindowAtom from "app/uiState/openWindowAtom";
import {
  ReactNode,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RecordScreenNavigationContext } from "./RecordScreenNavigationContext";
import { useSetAtom, useAtomValue, atom, useAtom, useStore } from "jotai";
import pageDimensionsAtom from "app/uiState/pageDimensionsAtom";
import { graphql } from "src/gql";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { getTenantId } from "app/tenant";
import { useCurrentComponent } from "app/useCurrentComponent";
import axios from "axios";
import pushComponentToStack from "app/uiState/mobile/pushComponentToStack";
import type {
  ComponentName,
  ComponentProps,
  ComponentState,
} from "app/componentOptions";
import {
  collection,
  getFirestore,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { JM_ENVIRONMENT, app } from "app/Firebase";
import { getApiUrl } from "lib/apiUrl";
import { ZodError, ZodObject, z } from "zod";
import { Permission } from "domain/application/permissions/Permissions";
import type { PermissionCheckInput } from "src/gql/graphql";
import { componentOptions } from "app/componentOptions";
import { queryClient } from "./queryClient";
import { fetchJmApiQuery } from "src/jmApiClient";
import { getAuth } from "firebase/auth";
import closeWindowAtom from "./uiState/closeWindowAtom";
import requestCloseWindowAtom from "./uiState/requestCloseWindowAtom";
import { uniqueId } from "lodash";
import { MakeOptional, resolve } from "src/gqty";
import { Policy } from "src/gql/graphql";
import removeComponentFromStack from "./uiState/mobile/removeComponentFromStack";
import resetComponentStackAtom from "./uiState/mobile/resetComponentStackAtom";
import { useOptionalRoutedComponentStack } from "./router/currentRoutedComponent";
import componentStackAtom from "./uiState/mobile/componentStackAtom";
import componentStackIdsAtom from "./uiState/mobile/ComponentStackIdsAtom";
import { useAtomCallback } from "jotai/utils";
import {
  getMobileComponentStackBaseHref,
  getMobileComponentStackHistoryBackSteps,
  getMobileComponentStackHistoryState,
  getMobileComponentStackRouteHref,
  isMobileComponentStackHistoryState,
} from "./router/mobileComponentStackRoutes";
import { getScreenRouteBackSteps, getScreenRouteBaseHref } from "./router/screenRoute";
import { getFirebaseEmulatorConfig } from "./firebaseEmulatorConfig";
import { resolveCurrentUser } from "./useCurrentUserData";
import openedWindowIdsAtom from "./uiState/openedWindowIdsAtom";

export { useCurrentComponent };
const firestore = getFirestore(app);
const auth = getAuth(app);

//type WindowModuleId = keyof typeof windowModules;

// Use this when wanting to open a window from an id supplied externally
// eg when loading portal items from the server
// export function isValidWindowModuleId(id: string): id is WindowModuleId {
//   return id in windowModules;
// }

export type OpenComponentState<T extends ComponentName> = MakeOptional<
  ComponentState<T>,
  "componentId" | "uiProps"
>;

export type OpenComponentOptions = {
  resetMobileStack?: boolean;
  background?: boolean;
  onClose?: () => void;
};

export type CloseComponentOptions = {
  skipDirtyCheck?: boolean;
};

export type OpenComponent = <T extends ComponentName>(
  componentState: OpenComponentState<T>,
  options?: OpenComponentOptions,
) => {
  closeComponent: (options?: CloseComponentOptions) => void;
};

export const useOpenComponent = (): OpenComponent => {
  const store = useStore();
  const recordNavigation = useContext(RecordScreenNavigationContext);
  // Default the entire parameter object to an empty object
  const isMobileOrTablet = useIsMobileOrTablet();

  const openWindow = useSetAtom(openWindowAtom);
  const closeWindow = useSetAtom(closeWindowAtom);
  const requestCloseWindow = useSetAtom(requestCloseWindowAtom);
  const openMobileComponent = useSetAtom(pushComponentToStack);
  const removeMobileComponent = useSetAtom(removeComponentFromStack);
  const resetMobileComponentStack = useSetAtom(resetComponentStackAtom);
  const routedComponentStack = useOptionalRoutedComponentStack();
  const removeMobileComponentImmediately = useAtomCallback(
    useCallback((get, set) => {
      const currentStack = get(componentStackAtom);

      if (currentStack.length === 0) {
        return;
      }

      const updatedStack = currentStack.slice(0, -1);
      set(componentStackAtom, updatedStack);
      set(
        componentStackIdsAtom,
        updatedStack.map((entry) => get(entry.componentAtom).componentId),
      );
    }, []),
  );

  return useCallback<OpenComponent>(
    <T extends ComponentName>(
      {
        componentId,
        componentType,
        componentProps,
        uiProps,
      }: OpenComponentState<T>,
      {
        resetMobileStack = false,
        background = false,
        onClose,
      }: OpenComponentOptions = {},
    ) => {
      if (componentType === "RecordScreen" && recordNavigation) {
        const destination = componentProps as
          | ComponentProps<"RecordScreen">
          | undefined;
        if (
          destination?.recordType === recordNavigation.recordType &&
          destination.recordId === recordNavigation.recordId
        ) {
          void recordNavigation.navigate(destination);
          return { closeComponent: () => {} };
        }
      }
      const finalComponentId = componentId
        ? `component-${componentId}`
        : uniqueId("component-");
      const trackClose = () => {
        if (!onClose) return;
        const idsAtom = isMobileOrTablet
          ? componentStackIdsAtom
          : openedWindowIdsAtom;
        // Observe removal after close guards accept, including window X and mobile Back.
        const unsubscribe = store.sub(idsAtom, () => {
          if (store.get(idsAtom).includes(finalComponentId)) return;
          unsubscribe();
          onClose();
        });
      };
      if (
        routedComponentStack?.entries.length &&
        !isMobileOrTablet &&
        !background
      ) {
        routedComponentStack.clearRouteComponents();
      }

      if (isMobileOrTablet) {
        const nextComponentState = {
          componentType,
          componentProps,
          uiProps: uiProps || {},
          componentId: finalComponentId,
        };

        void (async () => {
          if (resetMobileStack) {
            const reset = await resetMobileComponentStack();
            if (!reset) {
              return;
            }
            routedComponentStack?.clearRouteComponents();
          } else {
            const routedParent = routedComponentStack?.entries.at(-1);
            const currentStack = store.get(componentStackAtom);
            if (
              routedParent &&
              !currentStack.some(
                (entry) => entry.controller === routedParent.controller,
              )
            ) {
              // Promote the mounted route entry itself so its local UI and close guards survive Back.
              const parentStack = [...currentStack, routedParent.stackEntry];
              store.set(componentStackAtom, parentStack);
              store.set(
                componentStackIdsAtom,
                parentStack.map(
                  (entry) => store.get(entry.componentAtom).componentId,
                ),
              );
              routedComponentStack?.clearRouteComponents();
              window.history.replaceState(
                {
                  ...window.history.state,
                  ...getMobileComponentStackHistoryState(
                    parentStack.length,
                    // Transient routes share Home's entry instead of pushing a route.
                    (getScreenRouteBaseHref(window.history.state) ??
                      window.location.pathname + window.location.search) ===
                      getMobileComponentStackBaseHref(
                        JM_ENVIRONMENT,
                        getTenantId() ?? null,
                      )
                      ? 0
                      : parentStack.length,
                  ),
                },
                document.title,
                window.location.href,
              );
            }
          }

          const opened = await openMobileComponent(nextComponentState);

          if (!opened) {
            return;
          }

          trackClose();

          const tenantId = getTenantId() ?? null;
          const baseHref = getMobileComponentStackBaseHref(
            JM_ENVIRONMENT,
            tenantId,
          );
          const routeHref = getMobileComponentStackRouteHref(
            nextComponentState,
            JM_ENVIRONMENT,
            tenantId,
          );
          const nextDepth = store.get(componentStackAtom).length;

          if (
            resetMobileStack ||
            !window.history.state?.__jmMobileComponentStack
          ) {
            window.history.replaceState(
              getMobileComponentStackHistoryState(0),
              document.title,
              baseHref,
            );
          }

          window.history.pushState(
            getMobileComponentStackHistoryState(
              nextDepth,
              // Stack depth excludes folder entries in the parent screens.
              getMobileComponentStackHistoryBackSteps(window.history.state) + 1,
            ),
            document.title,
            routeHref,
          );
        })();

        return {
          closeComponent: ({ skipDirtyCheck } = {}) => {
            const hasStackHistory = isMobileComponentStackHistoryState(
              window.history.state,
            );
            const backSteps = getScreenRouteBackSteps(
              window.history.state,
              finalComponentId,
            );
            if (skipDirtyCheck === false) {
              void removeMobileComponent().then((removed) => {
                if (removed && hasStackHistory) window.history.go(-backSteps);
              });
              return;
            }

            removeMobileComponentImmediately();
            if (hasStackHistory) {
              window.history.go(-backSteps);
            }
          },
        };
      } else {
        openWindow(
          {
            componentType,
            componentProps,
            uiProps: uiProps || {},
            componentId: finalComponentId,
          },
          { background },
        );
        trackClose();
        return {
          closeComponent: ({ skipDirtyCheck } = {}) => {
            if (skipDirtyCheck === false) {
              requestCloseWindow(finalComponentId);
            } else {
              closeWindow(finalComponentId);
            }
          },
        };
      }
    },
    [
      recordNavigation,
      store,
      isMobileOrTablet,
      openMobileComponent,
      closeWindow,
      requestCloseWindow,
      openWindow,
      removeMobileComponent,
      removeMobileComponentImmediately,
      resetMobileComponentStack,
      routedComponentStack,
    ],
  );
};

export const useJmWebConfig = () => {
  return useFirestoreDocument(
    "jm-web-config/default",
    z.object({ latestVersion: z.string() }),
  );
};

export const useFrontendVersion = () => {
  return useQuery({
    queryKey: ["frontendVersion"],
    queryFn: async () => {
      const response = await fetch("/VERSION", {
        cache: "no-store",
      });
      if (!response.ok) {
        return null;
      }

      const version = (await response.text()).trim();
      return version === "" ? null : version;
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * @param firestorePathKey
 * @param documentSchema  Schema for an individual document in the collection
 * @param useQueryOptions
 * @returns
 */
export const useFirestoreCollection = <Schema extends ZodObject<any>>(
  firestorePathKey: string,
  documentSchema: Schema,
  useQueryOptions?: Omit<UseQueryOptions<Array<z.infer<Schema>>>, "queryKey">,
) => {
  const queryClient = useQueryClient();

  const [ref] = useState(() =>
    collection(firestore, firestorePathKey).withConverter<z.infer<Schema>>({
      toFirestore: (data) => ({ ...data }),
      fromFirestore: (snapshot) => {
        const data = snapshot.data();
        try {
          return documentSchema.parse(data);
        } catch (e) {
          if (e instanceof ZodError) {
            console.error(e.issues);
          }
        }
        throw new Error("Failed to parse document data");
      },
    }),
  );

  useEffect(() => {
    return onSnapshot(ref, (querySnapshot) => {
      queryClient.setQueryData(
        [firestorePathKey],
        querySnapshot.docs.map((d) => d.data()),
      );
    });
  }, [firestorePathKey, queryClient, ref]);

  return useQuery({
    queryKey: [firestorePathKey],
    queryFn: async () => {
      const querySnapshot = await getDocs(ref);
      const data = querySnapshot.docs.map((doc) => {
        return documentSchema.parse(doc.data());
      });
      return data;
    },
    ...useQueryOptions,
  });
};

// TODO: Combine with useFirestoreCollection?
export const useFirestoreDocument = <T extends ZodObject<any>>(
  firestorePathKey: string,
  schema: T,
  useQueryOptions?: Omit<UseQueryOptions<z.infer<T>>, "queryKey" | "queryFn">,
) => {
  const queryClient = useQueryClient();

  const [ref] = useState(() =>
    doc(firestore, firestorePathKey).withConverter<z.infer<T>>({
      toFirestore: (data) => {
        return schema.parse(data);
      },
      fromFirestore: (snapshot) => {
        const data = snapshot.data();
        return schema.parse(data);
      },
    }),
  );

  // Firestore snapshot listener to keep data up-to-date
  useEffect(() => {
    const unsubscribe = onSnapshot(ref, (querySnapshot) => {
      queryClient.setQueryData([firestorePathKey], querySnapshot.data());
    });

    return () => unsubscribe();
  }, [firestorePathKey, queryClient, ref]);

  // Provide a query function for initial data fetch
  return useQuery({
    queryKey: [firestorePathKey],
    queryFn: async () => {
      const snapshot = await getDoc(ref);
      const data = snapshot.data();
      return schema.parse(data);
    },
    ...useQueryOptions,
  });
};

export const useFirestoreDocumentMutation = (firestorePathKey: string) => {
  const [ref] = useState(() => doc(firestore, firestorePathKey));

  return useMutation({
    onMutate: async (data: any) => {
      await updateDoc(ref, data);
    },
  });
};

export const usePageDimensions = () => {
  return useAtomValue(pageDimensionsAtom);
};

export const isPermitted = async (
  permissionGroup: PermissionCheckInput[],
  conditionallyAllowed: boolean = false, // Returns true if the policy is conditional
): Promise<boolean> => {
  if (permissionGroup.length === 0) return false;

  const token = await auth.currentUser?.getIdTokenResult();
  if (token?.claims?.role === "admin") {
    return true;
  }

  const { currentUser } = await resolveCurrentUser();
  const permissions = currentUser?.permissions;

  if (!permissions) return false;

  const permissionPolicies = permissionGroup.map(({ permission }) =>
    permissions.find((policy) => policy.permission.id === permission),
  );
  if (
    permissionPolicies.some(
      (permissionPolicy) =>
        !permissionPolicy || permissionPolicy.policy === Policy.Deny,
    )
  ) {
    return false;
  }
  if (
    conditionallyAllowed ||
    permissionPolicies.every(
      (permissionPolicy) => permissionPolicy?.policy === Policy.Allow,
    )
  ) {
    return true;
  }

  // Conditional permissions need one server trip to evaluate the full group.
  const data = await fetchJmApiQuery(
    queryClient,
    isPermittedQueryDocument,
    { permissions: [...permissionGroup] },
    {
      refetchOnWindowFocus: false,
      //staleTime: 1000 * 60 * 60, // 60 minutes
    },
  );
  return data.isPermitted ?? false;
};

export const initAllowedComponents = async () => {
  const filterResults = await Promise.all(
    Object.entries(componentOptions)
      .filter(([key, cfg]) => cfg.showInLauncher)
      .map(async ([key, cfg]) => {
        if (cfg.canOpen) {
          const result = await cfg.canOpen();
          if (result) {
            return {
              componentId: key,
              ...cfg,
            };
          }
        }
        return false;
      }),
  );
  return filterResults.filter((r) => !!r);
};

export const useAllowedComponents = () => {
  const { data } = useQuery({
    queryKey: ["allowedComponents"],
    queryFn: initAllowedComponents,
  });
  return data;
};

export const prefetchAllowedComponents = async () => {
  await queryClient.prefetchQuery({
    queryKey: ["allowedComponents"],
    queryFn: initAllowedComponents,
  });
};

const isPermittedQueryDocument = graphql(`
  query isPermitted($permissions: [PermissionCheckInput!]!) {
    isPermitted(permissions: $permissions)
  }
`);

/**
 * We could do this "graphql-first" styles:
 * const result =
 */

export const useIsPermitted = (permissions: PermissionCheckInput[]) => {
  const { data } = useQuery({
    queryKey: ["isPermitted", permissions],
    queryFn: () => isPermitted(permissions),
  });

  return data ?? false;
};

export const useCurrentTenant = () => {
  const tenantId = getTenantId();
  if (tenantId === "jmadmin") {
    return {
      tenantId,
      displayName: "Job Manager Admin",
      firebaseTenantId: "jmadmin",
      themeColour: "#000000",
    };
  }
  const { data: axiosReponse } = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => axios.get(getApiUrl(`/api/tenant/${tenantId}`)),
    enabled: tenantId !== "jmadmin",
  });
  let publicTenantAssetsUrl;
  if (JM_ENVIRONMENT === "DEVELOPMENT") {
    if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true") {
      const firebaseEmulator = getFirebaseEmulatorConfig();
      publicTenantAssetsUrl = `https://${firebaseEmulator.host}:${firebaseEmulator.storagePort}/demo-job-manager.appspot.com/tenants/${tenantId}/public`;
    } else {
      publicTenantAssetsUrl = `https://firebasestorage.googleapis.com/v0/b/<YOUR_PROJECT_ID>.appspot.com/o/<ENCODED_FILE_PATH>?alt=media`;
    }
  } else if (JM_ENVIRONMENT === "STAGING") {
    publicTenantAssetsUrl = `https://storage.googleapis.com/staging-tenant-assets.${tenantId}.jobmgr.net`;
  } else {
    publicTenantAssetsUrl = `https://storage.googleapis.com/tenant-assets.${tenantId}.jobmgr.net`;
  }
  return {
    tenantId,
    publicTenantAssetsUrl,
    displayName: axiosReponse?.data?.displayName,
    firebaseTenantId: axiosReponse?.data?.firebaseTenantId,
    themeColour: axiosReponse?.data?.themeColour,
  };
};

// type GetAtomAtPath = <V extends object, P extends string>(
//   atom: WritableAtom<V, unknown[], unknown>,
//   path: P
// ) => WritableAtom<Get<V, P>, unknown[], SetStateAction<Get<V, P>>>;

// export const getAtomAtPath: GetAtomAtPath = <
//   A extends object,
//   P extends string,
// >(
//   atom: WritableAtom<A, unknown[], unknown>,
//   path: P
// ) => {
//   return atomFamily((path: string) =>
//     focusAtom(atom, (optic) => optic.path(path))
//   )(path) as any;
// };

// From: https://stackoverflow.com/a/11381730
// Note: This will return false for tablets. See above link for a version of this function that returns
export function mobileCheck() {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
  })(
    /** @ts-expect-error This is a browser check so we disable typechecking */
    navigator.userAgent || navigator.vendor || window.opera,
  );
  return check;
}

export const useIsMobile = () => {
  return useMemo(() => {
    return mobileCheck();
  }, []);
};

function tabletCheck() {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      (/(android|ipad|playbook|silk)/i.test(a) && !/mobile/i.test(a)) || // Check for tablets specifically
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
  })(
    /** @ts-expect-error we are doing a browser check */
    navigator.userAgent || navigator.vendor || window.opera,
  );
  return check;
}

export const useIsTablet = () => {
  return useMemo(() => {
    return tabletCheck();
  }, []);
};

export function mobileAndTabletCheck() {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
  })(
    /** @ts-expect-error we are doing a browser check */
    navigator.userAgent || navigator.vendor || window.opera,
  );
  return check;
}

export const useIsMobileOrTablet = () => {
  return useMemo(() => {
    return mobileAndTabletCheck();
  }, []);
};

const signoutAtom = atom(false);

export const useSignout = () => {
  return useAtom(signoutAtom);
};
